import { App, Form, Input, Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { userManagementService } from '@/services/api';

type PasswordValues = {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
};

/** Complete the existing self-password flow before any working screen mounts. */
export function MandatoryPasswordChange({ username }: { username?: string }): JSX.Element {
  const [form] = Form.useForm<PasswordValues>();
  const [saving, setSaving] = useState(false);
  const active = useRef(true);
  const { logout } = useAuth();
  const { message } = App.useApp();

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const exit = () => {
    active.current = false;
    form.resetFields();
    logout();
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (!active.current) return;
      setSaving(true);
      await userManagementService.changeOwnPassword({
        current_password: values.current_password.trim(),
        new_password: values.new_password.trim(),
      });
      if (!active.current) return;
      message.success('Пароль изменён');
      window.location.reload();
    } catch (error) {
      if (!active.current) return;
      if ((error as { errorFields?: unknown[] })?.errorFields) return;
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Не удалось изменить пароль');
    } finally {
      if (active.current) setSaving(false);
    }
  };

  return (
    <main className="appRouteLoader" aria-label="Обязательная смена временного пароля">
      <Modal
        open
        title={`Смена временного пароля${username ? `: ${username}` : ''}`}
        okText="Сохранить и продолжить"
        cancelText="Выйти"
        maskClosable={false}
        okButtonProps={{ loading: saving }}
        onCancel={exit}
        onOk={() => void submit()}
      >
        <p>Можно выйти и сменить пароль при следующем входе.</p>
        <Form<PasswordValues> form={form} layout="vertical">
          <Form.Item name="current_password" label="Текущий временный пароль"
            rules={[{ required: true, message: 'Введите временный пароль' }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item name="new_password" label="Новый пароль"
            rules={[{ required: true, message: 'Введите новый пароль' },
              { min: 8, message: 'Минимум 8 символов' }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="new_password_confirm" label="Повторите новый пароль"
            dependencies={['new_password']}
            rules={[{ required: true, message: 'Повторите новый пароль' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return !value || getFieldValue('new_password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('Пароли не совпадают'));
                },
              })]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </main>
  );
}
